import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ENV } from '@/lib/env';
import { Sentry } from '@/lib/sentry';
import { getAccessToken, useAuthStore } from '@/stores/auth';
import { broadcast } from './broadcast';

/**
 * axios client for the web app.
 *
 * Auth model:
 *   - Access token is held in memory (Zustand) and attached as
 *     `Authorization: Bearer …` on every request.
 *   - Refresh token lives in an httpOnly cookie set by the API. We never
 *     touch it from JS — the browser sends it automatically when we hit
 *     /auth/refresh with `withCredentials: true`.
 *   - 401 responses trigger a single in-flight refresh; concurrent requests
 *     queue until the refresh resolves, then retry with the new token.
 *   - On refresh failure → clear auth state, broadcast 'signout' to other
 *     tabs, route to /welcome via the auth store's status flip.
 *
 * The retry/queue logic mirrors apps/mobile/src/services/api.ts but is
 * cookie-driven instead of body-driven (no refresh token in JS).
 */

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const api: AxiosInstance = axios.create({
  baseURL: ENV.apiUrl,
  timeout: 30_000,
  withCredentials: true, // send the bz_rt cookie on /auth/refresh
  headers: {
    'X-Client': 'web', // backend uses this to omit refresh token from JSON body
  },
});

// --- Request interceptor: attach access token ----------------------------
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && !config.headers.has('Authorization')) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// --- 401 refresh queue ----------------------------------------------------
let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

function flushQueue(token: string | null): void {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await axios.post<{ accessToken: string }>(
      `${ENV.apiUrl}/auth/refresh`,
      {},
      {
        withCredentials: true,
        headers: {
          'X-Requested-With': 'fetch', // CSRF guard required by the API
          'X-Client': 'web',
        },
      },
    );
    const accessToken = res.data?.accessToken;
    if (!accessToken) throw new Error('No accessToken in refresh response');
    useAuthStore.getState().setAccessToken(accessToken);
    return accessToken;
  } catch {
    return null;
  }
}

// --- Response interceptor: refresh + retry on 401 ------------------------
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;

    if (!original || error.response?.status !== 401) {
      reportToSentry(error);
      return Promise.reject(error);
    }

    // Skip refresh for the refresh endpoint itself or for the unauthenticated
    // /auth/* mutations (login/register/social) — there's no token to refresh.
    if (
      original.url?.includes('/auth/refresh') ||
      original.url?.includes('/auth/login') ||
      original.url?.includes('/auth/register') ||
      original.url?.includes('/auth/social')
    ) {
      return Promise.reject(error);
    }

    if (original._retry) {
      // Already retried once — give up.
      handleRefreshFailure();
      return Promise.reject(error);
    }
    original._retry = true;

    if (isRefreshing) {
      // Queue this request until the in-flight refresh resolves.
      return new Promise((resolve, reject) => {
        pendingQueue.push((token) => {
          if (!token) {
            reject(error);
            return;
          }
          original.headers.set('Authorization', `Bearer ${token}`);
          resolve(api(original));
        });
      });
    }

    isRefreshing = true;
    try {
      const token = await refreshAccessToken();
      flushQueue(token);
      if (!token) {
        handleRefreshFailure();
        return Promise.reject(error);
      }
      original.headers.set('Authorization', `Bearer ${token}`);
      return api(original);
    } finally {
      isRefreshing = false;
    }
  },
);

function handleRefreshFailure(): void {
  useAuthStore.getState().clear();
  broadcast({ type: 'signout' });
}

function reportToSentry(error: AxiosError): void {
  const status = error.response?.status ?? 0;
  // 4xx (except 429) is usually expected validation/auth — only escalate 5xx
  // and unexpected network errors. Mirrors the mobile pattern.
  if (status >= 500 || status === 0) {
    Sentry.captureException(error, {
      tags: { domain: 'api', status: String(status) },
      extra: {
        url: error.config?.url,
        method: error.config?.method,
      },
    });
  }
}

/** Helper for callers that want a typed GET. */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.get<T>(url, config);
  return res.data;
}

export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await api.post<T>(url, body, config);
  return res.data;
}
